import React, { useRef, useEffect, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import { VisualizerState } from '../../types/vehicle';
import {
  renderVisualizerScene,
  exportCanvasSnapshot,
  CANVAS_VIRTUAL_WIDTH,
  CANVAS_VIRTUAL_HEIGHT,
} from '../../utils/canvasRenderer';
import {
  CameraPresetId,
  LightingPresetId,
  CAMERA_PRESETS,
  MODEL_CONFIGS,
} from '../../types/threeStudio';
import type { ThreeCarRenderer } from '../../utils/threeCarRenderer';
import {
  Camera,
  RotateCw,
  Layers,
  Crosshair,
  Compass,
} from 'lucide-react';

export interface StanceCanvasRef {
  exportSnapshot: (filename?: string) => string;
  getCanvasElement: () => HTMLCanvasElement | null;
}

export interface StanceCanvasProps {
  state: VisualizerState;
  onUpdateState?: (updates: Partial<VisualizerState>) => void;
  className?: string;
  allowPinning?: boolean;
}

function checkWebGLSupport(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const canvas = document.createElement('canvas');
    return !!(
      window.WebGLRenderingContext &&
      (canvas.getContext('webgl2') || canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))
    );
  } catch (e) {
    return false;
  }
}

export const StanceCanvas = forwardRef<StanceCanvasRef, StanceCanvasProps>(
  ({ state, onUpdateState, className = '', allowPinning = false }, ref) => {
    // 2D Canvas Fallback Refs & State
    const canvas2dRef = useRef<HTMLCanvasElement | null>(null);
    const [customImage, setCustomImage] = useState<HTMLImageElement | null>(null);
    const [pinningStep, setPinningStep] = useState<'idle' | 'front' | 'rear'>('idle');

    // 3D WebGL Studio Refs & State
    const threeContainerRef = useRef<HTMLDivElement | null>(null);
    const threeRendererRef = useRef<ThreeCarRenderer | null>(null);
    const [renderMode, setRenderMode] = useState<'3d' | '2d'>('3d');
    const [hasWebGL, setHasWebGL] = useState<boolean>(true);
    const [is3dLoaded, setIs3dLoaded] = useState<boolean>(false);
    const [activeCameraAngle, setActiveCameraAngle] = useState<CameraPresetId>('beauty_3_4');
    const [lightingMode, setLightingMode] = useState<LightingPresetId>('industrial_shop');
    const [isAutoRotating, setIsAutoRotating] = useState<boolean>(false);

    // Initial check for WebGL support
    useEffect(() => {
      const supported = checkWebGLSupport();
      setHasWebGL(supported);
      if (!supported) {
        setRenderMode('2d');
      }
    }, []);

    // Preload custom silhouette image for 2D fallback or custom upload
    useEffect(() => {
      if (state.silhouetteId === 'custom_upload' && state.customSilhouetteDataUrl) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => setCustomImage(img);
        img.src = state.customSilhouetteDataUrl;
      } else {
        setCustomImage(null);
      }
    }, [state.silhouetteId, state.customSilhouetteDataUrl]);

    // Redraw 2D canvas on state or image change
    const render2DScene = useCallback(() => {
      if (!canvas2dRef.current) return;
      renderVisualizerScene(canvas2dRef.current, state, {
        customImage,
        pinningMode: pinningStep !== 'idle',
      });
    }, [state, customImage, pinningStep]);

    useEffect(() => {
      if (renderMode === '2d' || !hasWebGL) {
        render2DScene();
      }
    }, [renderMode, hasWebGL, render2DScene]);

    // Initialize 3D Three.js Studio Engine
    useEffect(() => {
      if (renderMode !== '3d' || !hasWebGL || !threeContainerRef.current) return;
      let active = true;

      import('../../utils/threeCarRenderer')
        .then(({ ThreeCarRenderer }) => {
          if (!active || !threeContainerRef.current) return;

          const renderer3d = new ThreeCarRenderer();
          threeRendererRef.current = renderer3d;

          renderer3d.mount(threeContainerRef.current, state, {
            onLoaded: () => {
              if (active) setIs3dLoaded(true);
            },
            onError: (err) => {
              console.warn('3D Studio encountered error, falling back to 2D Blueprint:', err);
              if (active) setRenderMode('2d');
            },
          });
        })
        .catch((err) => {
          console.warn('Failed to load ThreeCarRenderer module, falling back to 2D:', err);
          if (active) setRenderMode('2d');
        });

      return () => {
        active = false;
        threeRendererRef.current?.destroy();
        threeRendererRef.current = null;
        setIs3dLoaded(false);
      };
      // Re-mount only when renderMode changes
    }, [renderMode, hasWebGL]);

    // Hot-update 3D Studio scene when visualizer state changes (paint, stance, wheels, silhouette)
    useEffect(() => {
      if (renderMode === '3d' && threeRendererRef.current) {
        threeRendererRef.current.update(state);
      }
    }, [state, renderMode]);

    // Camera preset switch handler
    const handleCameraPreset = (presetId: CameraPresetId) => {
      setActiveCameraAngle(presetId);
      if (threeRendererRef.current) {
        threeRendererRef.current.setCameraAngle(presetId);
      }
    };

    // Lighting preset switch handler
    const handleLightingMode = (mode: LightingPresetId) => {
      setLightingMode(mode);
      if (threeRendererRef.current) {
        threeRendererRef.current.setLightingEnvironment(mode);
      }
    };

    // Turntable auto-rotate toggle
    const handleToggleAutoRotate = () => {
      const next = !isAutoRotating;
      setIsAutoRotating(next);
      if (threeRendererRef.current) {
        threeRendererRef.current.setTurntableAutoRotate(next);
      }
    };

    // Expose snapshot export API to parents
    useImperativeHandle(ref, () => ({
      exportSnapshot: (filename?: string) => {
        const defaultName = `restomod-${state.silhouetteId}-${state.paint.presetName?.toLowerCase().replace(/\s+/g, '-') || 'custom'}.png`;
        const name = filename || defaultName;

        if (renderMode === '3d' && threeRendererRef.current) {
          return threeRendererRef.current.exportSnapshot(name);
        }

        if (canvas2dRef.current) {
          return exportCanvasSnapshot(canvas2dRef.current, name);
        }

        return '';
      },
      getCanvasElement: () => {
        if (renderMode === '3d' && threeContainerRef.current) {
          return threeContainerRef.current.querySelector('canvas');
        }
        return canvas2dRef.current;
      },
    }));

    // Handle 2D canvas click during hub pinning
    const handleCanvasClick = (_e: React.MouseEvent<HTMLCanvasElement>) => {
      if (pinningStep === 'idle' || !canvas2dRef.current || !onUpdateState) return;

      if (pinningStep === 'front') {
        setPinningStep('rear');
      } else if (pinningStep === 'rear') {
        setPinningStep('idle');
      }
    };

    // Calculate real-time suspension stance telemetry
    const frontOffset = state.stance.frontHeightOffsetInches;
    const rearOffset = state.stance.rearHeightOffsetInches;
    const rakeInches = rearOffset - frontOffset;
    const wheelbaseIn = 108.0;
    const rakeAngleDeg = ((Math.atan2(rakeInches, wheelbaseIn) * 180) / Math.PI).toFixed(2);

    let rakeClassification = 'Level Resto';
    if (rakeInches > 1.5) rakeClassification = 'Aggressive Drag Rake';
    else if (rakeInches > 0.5) rakeClassification = 'Pro-Touring Stance';
    else if (rakeInches < -0.5) rakeClassification = 'California Gasser Rake';
    if (frontOffset <= -3.0 && rearOffset <= -3.0) rakeClassification = 'Slammed Low-Profile';

    return (
      <div
        className={`relative w-full overflow-hidden bg-shop-darkest border border-shop-border rounded select-none flex flex-col ${className}`}
      >
        {/* 1. Main Viewport Container */}
        <div className="relative w-full aspect-[16/9] sm:aspect-[2/1] min-h-[360px] bg-shop-darkest overflow-hidden">
          {/* 3D WebGL Three.js Container */}
          {renderMode === '3d' && hasWebGL && (
            <div
              ref={threeContainerRef}
              className="absolute inset-0 w-full h-full cursor-grab active:cursor-grabbing"
              title="Click and drag to rotate vehicle in 3D. Scroll to zoom. Right-click drag to pan."
            />
          )}

          {/* 2D HTML5 Canvas Fallback Viewport */}
          {(renderMode === '2d' || !hasWebGL) && (
            <canvas
              ref={canvas2dRef}
              width={CANVAS_VIRTUAL_WIDTH}
              height={CANVAS_VIRTUAL_HEIGHT}
              onClick={handleCanvasClick}
              className={`w-full h-full object-contain block cursor-default ${
                pinningStep !== 'idle' ? 'cursor-crosshair' : ''
              }`}
              style={{ imageRendering: 'auto' }}
            />
          )}

          {/* HUD Top Left Badges: Model & Coating */}
          <div className="absolute top-3 left-3 flex flex-wrap items-center gap-2 pointer-events-none z-10">
            <span className="font-mono text-[10px] font-bold tracking-wider px-2 py-1 bg-shop-dark/95 border border-shop-border text-shop-highlight rounded uppercase shadow-sm flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-safety-amber animate-pulse"></span>
              {state.silhouetteId.replace('_', ' ')}
            </span>
            <span className="font-mono text-[10px] font-bold px-2 py-1 bg-shop-panel/95 border border-shop-border text-safety-amber rounded uppercase shadow-sm">
              {state.paint.presetName || state.paint.finishType.replace('_', ' ')}
            </span>
            <span className="hidden sm:inline-flex font-mono text-[10px] font-bold px-2 py-1 bg-shop-darkest/90 border border-shop-border text-blueprint-cyan rounded uppercase shadow-sm">
              {renderMode === '3d' ? '3D PBR WebGL' : '2D CAD Blueprint'}
            </span>
            {MODEL_CONFIGS[state.silhouetteId]?.isPlaceholderMesh && (
              <span
                className="font-mono text-[10px] font-bold px-2 py-1 bg-safety-amber/15 border border-safety-amber text-safety-amber rounded uppercase shadow-sm"
                title={MODEL_CONFIGS[state.silhouetteId]?.meshLayoutNote}
              >
                Placeholder Mesh
              </span>
            )}
          </div>

          {/* HUD Top Right Action Controls: Engine Mode & Snapshot */}
          <div className="absolute top-3 right-3 flex items-center gap-2 z-10">
            {hasWebGL && (
              <button
                onClick={() => setRenderMode((m) => (m === '3d' ? '2d' : '3d'))}
                className="min-h-[44px] px-3 py-1.5 rounded bg-shop-panel hover:bg-shop-well text-shop-highlight border border-shop-border shadow-sm flex items-center gap-1.5 text-[11px] font-mono font-bold uppercase tracking-wider cursor-pointer transition-all duration-100"
                title={`Switch to ${renderMode === '3d' ? '2D CAD Blueprint' : '3D WebGL Studio'}`}
              >
                <Layers className="w-4 h-4 text-blueprint-cyan shrink-0" />
                <span>{renderMode === '3d' ? '2D CAD' : '3D WebGL'}</span>
              </button>
            )}

            {allowPinning && state.silhouetteId === 'custom_upload' && renderMode === '2d' && (
              <button
                onClick={() => setPinningStep((prev) => (prev === 'idle' ? 'front' : 'idle'))}
                className={`min-h-[44px] px-3 py-1.5 rounded border text-xs font-mono font-bold uppercase tracking-wider flex items-center gap-1.5 cursor-pointer transition-all duration-100 ${
                  pinningStep !== 'idle'
                    ? 'bg-safety-amber text-shop-darkest border-safety-amberDark shadow-sm'
                    : 'bg-shop-panel text-shop-light border-shop-border hover:bg-shop-well'
                }`}
                title="Pin Front & Rear Wheel Hubs on Custom Upload"
              >
                <Crosshair className="w-4 h-4 shrink-0" />
                <span>{pinningStep === 'idle' ? 'Pin Hubs' : `Pinning: ${pinningStep.toUpperCase()}`}</span>
              </button>
            )}

            <button
              onClick={() => {
                const name = `restomod-${state.silhouetteId}-${state.paint.presetName?.toLowerCase().replace(/\s+/g, '-') || 'custom'}.png`;
                if (renderMode === '3d' && threeRendererRef.current) {
                  threeRendererRef.current.exportSnapshot(name);
                } else if (canvas2dRef.current) {
                  exportCanvasSnapshot(canvas2dRef.current, name);
                }
              }}
              className="min-h-[44px] min-w-[44px] px-3 py-1.5 rounded bg-shop-panel hover:bg-shop-well text-shop-highlight border border-shop-border shadow-sm flex items-center gap-1.5 text-xs font-mono font-bold uppercase tracking-wider cursor-pointer active:translate-y-0.5 transition-all duration-100"
              title="Download Hi-Res Snapshot PNG"
            >
              <Camera className="w-4 h-4 text-safety-amber shrink-0" />
              <span className="hidden md:inline">Snapshot PNG</span>
            </button>
          </div>

          {/* HUD Bottom Left: Suspension & Kinematics Telemetry */}
          <div className="absolute bottom-3 left-3 bg-shop-darkest/95 border border-shop-border rounded p-2.5 font-mono text-[10px] text-shop-light shadow-md pointer-events-none z-10 max-w-[280px]">
            <div className="flex items-center justify-between text-safety-amber font-bold border-b border-shop-border/80 pb-1 mb-1.5">
              <span>SUSPENSION RAKE HUD</span>
              <span className="text-[9px] uppercase px-1 rounded bg-shop-panel border border-shop-border text-shop-highlight">
                {rakeClassification}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
              <div>
                FRONT: <strong className="text-shop-highlight">{frontOffset >= 0 ? `+${frontOffset.toFixed(1)}"` : `${frontOffset.toFixed(1)}"`}</strong>
              </div>
              <div>
                REAR: <strong className="text-shop-highlight">{rearOffset >= 0 ? `+${rearOffset.toFixed(1)}"` : `${rearOffset.toFixed(1)}"`}</strong>
              </div>
              <div>
                RAKE ANGLE: <strong className="text-safety-cyan">{rakeAngleDeg}°</strong>
              </div>
              <div>
                WHEELS: <strong className="text-shop-highlight">{state.stance.frontWheelDiameter}" / {state.stance.rearWheelDiameter}"</strong>
              </div>
            </div>
          </div>

          {/* 3D Loading Overlay */}
          {renderMode === '3d' && !is3dLoaded && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-shop-darkest/90 z-20">
              <div className="w-8 h-8 border-2 border-shop-border border-t-safety-amber rounded-full animate-spin mb-3"></div>
              <p className="font-mono text-xs font-bold uppercase tracking-wider text-shop-light">
                Initializing 3D WebGL Studio...
              </p>
              <p className="font-mono text-[10px] text-shop-muted mt-1">
                Loading vehicle geometry, PBR shaders &amp; turntable platform
              </p>
            </div>
          )}
        </div>

        {/* 2. Studio Camera & Lighting Control Deck (Machined Tool Strip) */}
        {renderMode === '3d' && (
          <div className="bg-shop-dark border-t border-shop-border p-2.5 flex flex-wrap items-center justify-between gap-3 text-xs font-mono">
            {/* Camera View Angle Selector */}
            <div className="flex items-center gap-1.5 overflow-x-auto py-0.5">
              <span className="text-[11px] font-bold text-shop-muted uppercase flex items-center gap-1 mr-1 shrink-0">
                <Compass className="w-3.5 h-3.5 text-safety-amber" />
                <span>Angle:</span>
              </span>
              {(Object.keys(CAMERA_PRESETS) as CameraPresetId[]).map((key) => {
                const preset = CAMERA_PRESETS[key];
                const isActive = activeCameraAngle === key;
                return (
                  <button
                    key={key}
                    onClick={() => handleCameraPreset(key)}
                    className={`min-h-[44px] px-3 py-2 rounded border text-[11px] font-bold uppercase tracking-wide transition-all duration-100 cursor-pointer shrink-0 ${
                      isActive
                        ? 'bg-shop-panel text-safety-amber border-safety-amber shadow-[inset_0_1px_0_0_#f59e0b]'
                        : 'bg-shop-darkest text-shop-muted border-shop-border hover:bg-shop-well hover:text-shop-light'
                    }`}
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>

            {/* Lighting Rigs & Turntable Deck */}
            <div className="flex items-center gap-2 ml-auto shrink-0">
              {/* Turntable Auto-Rotate Toggle */}
              <button
                onClick={handleToggleAutoRotate}
                className={`min-h-[44px] px-3 py-2 rounded border text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5 transition-all duration-100 cursor-pointer ${
                  isAutoRotating
                    ? 'bg-safety-amber text-shop-darkest border-safety-amberDark shadow-sm'
                    : 'bg-shop-darkest text-shop-muted border-shop-border hover:bg-shop-well hover:text-shop-light'
                }`}
                title="Toggle 360 Turntable Showroom Spin"
              >
                <RotateCw className={`w-3.5 h-3.5 ${isAutoRotating ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">360° Spin</span>
              </button>

              {/* Lighting Environment Selector */}
              <div className="flex items-center gap-1 bg-shop-darkest p-1 rounded border border-shop-border">
                <button
                  onClick={() => handleLightingMode('industrial_shop')}
                  className={`min-h-[44px] px-3 py-1.5 rounded text-[11px] font-bold uppercase transition-all cursor-pointer ${
                    lightingMode === 'industrial_shop'
                      ? 'bg-shop-panel text-shop-highlight border border-shop-steel'
                      : 'text-shop-muted hover:text-shop-light'
                  }`}
                  title="Industrial Shop Overhead Fluorescents"
                >
                  Shop
                </button>
                <button
                  onClick={() => handleLightingMode('showroom_spotlight')}
                  className={`min-h-[44px] px-3 py-1.5 rounded text-[11px] font-bold uppercase transition-all cursor-pointer ${
                    lightingMode === 'showroom_spotlight'
                      ? 'bg-shop-panel text-shop-highlight border border-shop-steel'
                      : 'text-shop-muted hover:text-shop-light'
                  }`}
                  title="High-Contrast Showroom Spotlights"
                >
                  Spotlight
                </button>
                <button
                  onClick={() => handleLightingMode('daylight')}
                  className={`min-h-[44px] px-3 py-1.5 rounded text-[11px] font-bold uppercase transition-all cursor-pointer ${
                    lightingMode === 'daylight'
                      ? 'bg-shop-panel text-shop-highlight border border-shop-steel'
                      : 'text-shop-muted hover:text-shop-light'
                  }`}
                  title="Natural Golden Daylight"
                >
                  Daylight
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
);

StanceCanvas.displayName = 'StanceCanvas';
